/**
 * Service for group member management business logic
 */
import type { GroupWithDashboard } from '@splitfiesta/shared';

export interface PendingInvite {
  id: string;
  email: string;
  invitedBy: { name: string };
  invitedAt: string;
  status: 'pending' | 'accepted' | 'rejected';
}

export interface GroupMemberValidationResult {
  isValid: boolean;
  error?: string;
}

class GroupMemberService {
  /**
   * Validate email before adding as member
   */
  validateMemberEmail(
    email: string,
    _group: GroupWithDashboard | null,
    pendingInvites: PendingInvite[],
    t: (key: string, params?: Record<string, unknown>) => string
  ): GroupMemberValidationResult {
    if (!email.trim()) {
      return {
        isValid: false,
        error: t('members:errors.enterEmail'),
      };
    }

    // Check if email is already a member
    // Note: GroupWithDashboard.balances doesn't include email, so we'll skip this check for now
    // TODO: Implement proper member email checking with a dedicated members endpoint
    const existingMember = null;
    if (existingMember) {
      return {
        isValid: false,
        error: t('members:errors.alreadyMember'),
      };
    }

    // Check if already invited
    const existingInvite = pendingInvites.find(
      invite =>
        invite.email.toLowerCase() === email.toLowerCase() &&
        invite.status === 'pending'
    );
    if (existingInvite) {
      return {
        isValid: false,
        error: t('members:errors.alreadyInvited'),
      };
    }

    return { isValid: true };
  }

  /**
   * Sort group members by priority: owner first, current user, then alphabetically
   */
  sortGroupMembers(
    balances: GroupWithDashboard['balances'],
    ownerId: string,
    currentUserId?: string
  ) {
    if (!balances) return [];

    return [...balances].sort((a, b) => {
      const aIsOwner = a.userId === ownerId;
      const bIsOwner = b.userId === ownerId;
      const aIsCurrentUser = a.userId === currentUserId;
      const bIsCurrentUser = b.userId === currentUserId;

      // Owner always comes first
      if (aIsOwner && !bIsOwner) return -1;
      if (!aIsOwner && bIsOwner) return 1;

      // If neither is owner, current user comes next
      if (!aIsOwner && !bIsOwner) {
        if (aIsCurrentUser && !bIsCurrentUser) return -1;
        if (!aIsCurrentUser && bIsCurrentUser) return 1;
      }

      // Otherwise, sort alphabetically by name
      const aName = a.userName || `Member ${a.userId.slice(-4)}`;
      const bName = b.userName || `Member ${b.userId.slice(-4)}`;
      return aName.localeCompare(bName);
    });
  }

  /**
   * Get exclude IDs for friend selector (current members)
   */
  getMemberExcludeIds(group: GroupWithDashboard | null): string[] {
    return group?.balances?.map(balance => balance.userId) || [];
  }

  /**
   * Check if user is group owner
   */
  isGroupOwner(
    user: { id: string } | null,
    group: GroupWithDashboard | null
  ): boolean {
    return !!(group && group.userRole === 'owner');
  }

  /**
   * Handle different error types with user-friendly messages
   */
  handleMemberError(
    error: unknown,
    t: (key: string, params?: Record<string, unknown>) => string
  ): string {
    const errorMessage = error instanceof Error ? error.message : '';

    if (
      errorMessage.includes('Unexpected token') ||
      errorMessage.includes('<!DOCTYPE')
    ) {
      return t('common:errors.serverError');
    } else if (
      errorMessage.includes('404') ||
      errorMessage.includes('Not Found')
    ) {
      return t('common:errors.notFound');
    } else if (errorMessage.includes('401') || errorMessage.includes('403')) {
      return t('common:errors.accessDenied');
    } else if (
      errorMessage.includes('500') ||
      errorMessage.includes('Internal Server Error')
    ) {
      return t('common:errors.internalError');
    } else if (
      errorMessage.includes('Network Error') ||
      errorMessage.includes('fetch')
    ) {
      return t('common:errors.networkError');
    } else {
      return error instanceof Error
        ? error.message
        : t('members:errors.removeFailed');
    }
  }

  /**
   * Format success message for adding multiple friends
   */
  formatFriendsAddedMessage(
    count: number,
    t: (key: string, params?: Record<string, unknown>) => string
  ): string {
    return t('members:messages.friendsAdded', { count });
  }

  /**
   * Format success message for adding single member
   */
  formatMemberAddedMessage(
    email: string,
    t: (key: string, params?: Record<string, unknown>) => string
  ): string {
    return t('members:messages.memberAdded', { email });
  }

  /**
   * Format success message for removing member
   */
  formatMemberRemovedMessage(
    userName: string,
    t: (key: string, params?: Record<string, unknown>) => string
  ): string {
    return t('members:messages.memberRemoved', { name: userName });
  }

  /**
   * Format confirmation message for removing member
   */
  formatRemoveConfirmation(
    userName: string,
    t: (key: string, params?: Record<string, unknown>) => string
  ): string {
    return t('members:messages.confirmRemove', { name: userName });
  }

  /**
   * Format confirmation message for canceling invite
   */
  formatCancelInviteConfirmation(
    email: string,
    t: (key: string, params?: Record<string, unknown>) => string
  ): string {
    return t('members:messages.confirmCancelInvite', { email });
  }

  /**
   * Format success message for canceling invitation
   */
  formatInvitationCancelledMessage(
    email: string,
    t: (key: string, params?: Record<string, unknown>) => string
  ): string {
    return t('members:messages.invitationCancelled', { email });
  }

  /**
   * Format success message for resending invitation
   */
  formatInvitationResentMessage(
    email: string,
    t: (key: string, params?: Record<string, unknown>) => string
  ): string {
    return t('members:messages.invitationResent', { email });
  }
}

export const groupMemberService = new GroupMemberService();
